import { type IconName, type IconPrefix, findIconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, type AutocompleteProps, Box, TextField } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { useMemo, useState } from 'react';

type OptionType = {
  pkg: IconPrefix;
  label: IconName;
  id: string;
};

type Props = {
  value: OptionType | null;

  autoCompleteProps?: Omit<AutocompleteProps<OptionType, false, false, false>, 'options' | 'renderInput' | 'value'>;
  /**
   * @function onChange
   * @param icon - the package and icon name concatenated with a space e.g. "fas fa-user"
   */
  onChange?: (icon: string | null) => void;
  packages?: IconPrefix[];
  disablePortal?: boolean;
};

const IconAutocomplete = ({
  value = null,
  packages = ['fas', 'fab'],
  ...props
}: Props &
  Omit<AutocompleteProps<OptionType, false, false, false>, 'renderInput' | 'options' | 'onChange' | 'value'>) => {
  const [inputValue, setInputValue] = useState<OptionType | null>(value);

  const options = useMemo<OptionType[]>(() => {
    return packages
      .flatMap((pkg) => {
        // biome-ignore lint/suspicious/noExplicitAny: no other way to access the definitions
        const def = (library as any)?.definitions?.[pkg];
        if (!def) return;

        return Object.keys(def).map((key, idx) => {
          const icon = findIconDefinition({ prefix: pkg, iconName: key as IconName });
          if (!icon) return;
          return {
            pkg,
            label: icon.iconName,
            id: `${pkg}_${idx}_${icon.iconName}`,
          };
        });
      })
      .filter((icon) => !!icon);
  }, []);

  return (
    <Autocomplete
      disablePortal={props.disablePortal}
      options={options}
      size="small"
      {...props.autoCompleteProps}
      disabled={props?.disabled}
      sx={{ minWidth: '233px' }}
      renderInput={(params) => (
        <TextField
          label="Icon"
          {...params}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: !!inputValue && (
                <FontAwesomeIcon icon={[inputValue.pkg, inputValue.label as IconName]} style={{ margin: '0 0.5em' }} />
              ),
            },
          }}
        />
      )}
      isOptionEqualToValue={(option, value) => {
        return value.label === option.label;
      }}
      getOptionLabel={(option) => {
        return option.label;
      }}
      renderOption={(props, option) => {
        return (
          <Box
            component="li"
            {...props}
            style={{ padding: '0.5em 0.5em' }}
            key={`${option.id}_${option.label}_${option.pkg}`}
          >
            <FontAwesomeIcon icon={[option.pkg, option.label as IconName]} style={{ margin: '0 0.5em' }} />
            {option.label}
          </Box>
        );
      }}
      filterOptions={(options, { inputValue }) => {
        return matchSorter(options, inputValue, { keys: ['label'] });
      }}
      value={inputValue}
      onChange={(e, option) => {
        setInputValue(option);
        props?.onChange?.(!!option ? `${option?.pkg} ${option?.label}` : null);
      }}
    />
  );
};

export const faParseIconName = (
  iconName: string | null | undefined,
  defaultPkg = 'fas' as OptionType['pkg']
): OptionType | null => {
  if (!iconName || iconName === '') return null;
  const tokens = iconName.split(' ');

  if (tokens.length === 1) return { pkg: defaultPkg, label: tokens[0] as IconName, id: `${defaultPkg}_${tokens[0]}` };
  if (tokens.length === 2)
    return { pkg: tokens[0] as IconPrefix, label: tokens[1] as IconName, id: `${tokens[0]}_${tokens[1]}` };

  return { pkg: defaultPkg, label: iconName as IconName, id: `fas_${iconName}` };
};

export default IconAutocomplete;
